import { useMutation } from "@tanstack/react-query";
import {
  authClient,
  type BetterAuthClientOnErrorContext,
} from "@cartz/auth/client";

// Reset Password
interface ResetPasswordInput {
  newPassword: string;
  token: string;
}

interface ResetPasswordOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const useResetPassword = (options?: ResetPasswordOptions) => {
  return useMutation({
    mutationFn: async ({ newPassword, token }: ResetPasswordInput) => {
      return new Promise<void>((resolve, reject) => {
        authClient.resetPassword(
          {
            newPassword,
            token,
          },
          {
            onSuccess: () => resolve(),
            onError: (ctx: BetterAuthClientOnErrorContext) =>
              reject(new Error(ctx?.error.message)),
          },
        );
      });
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
};

// Forgot Password
interface ForgotPasswordInput {
  email: string;
}

interface ForgotPasswordOptions {
  redirectTo: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const useForgotPassword = (options: ForgotPasswordOptions) => {
  return useMutation({
    mutationFn: async ({ email }: ForgotPasswordInput) => {
      return new Promise<void>((resolve, reject) => {
        authClient.requestPasswordReset(
          {
            email,
            redirectTo: options.redirectTo,
          },
          {
            onSuccess: () => resolve(),
            onError: (ctx: BetterAuthClientOnErrorContext) =>
              reject(new Error(ctx.error.message)),
          },
        );
      });
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
};

// Sign Up
interface SignUpInput {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

interface SignUpOptions {
  callbackURL: string;
  onSuccess?: (email: string) => void;
  onError?: (error: Error) => void;
}

export const useSignUp = (options: SignUpOptions) => {
  return useMutation({
    mutationFn: async ({
      email,
      password,
      firstName,
      lastName,
    }: SignUpInput) => {
      return new Promise<void>((resolve, reject) => {
        authClient.signUp.email(
          {
            email,
            password,
            name: `${firstName} ${lastName}`,
            callbackURL: options.callbackURL,
          },
          {
            onSuccess: () => resolve(),
            onError: (ctx: BetterAuthClientOnErrorContext) =>
              reject(new Error(ctx.error.message)),
          },
        );
      });
    },
    onSuccess: (_, variables) => {
      options.onSuccess?.(variables.email);
    },
    onError: options.onError,
  });
};

// Sign In
interface SignInInput {
  email: string;
  password: string;
}

interface SignInOptions {
  callbackURL: string;
  onEmailNotVerified?: (email: string) => void;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const useSignIn = (options: SignInOptions) => {
  return useMutation({
    mutationFn: async ({ email, password }: SignInInput) => {
      return new Promise<void>((resolve, reject) => {
        authClient.signIn.email(
          {
            email,
            password,
            callbackURL: options.callbackURL,
          },
          {
            onSuccess: () => resolve(),
            onError: async (ctx: BetterAuthClientOnErrorContext) => {
              if (ctx.error.code === "EMAIL_NOT_VERIFIED") {
                options.onEmailNotVerified?.(email);
                resolve();
                return;
              }
              reject(new Error(ctx.error.message));
            },
          },
        );
      });
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
};

// Send Verification Email
interface SendVerificationEmailInput {
  email: string;
}

interface SendVerificationEmailOptions {
  callbackURL: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const useSendVerificationEmail = (
  options: SendVerificationEmailOptions,
) => {
  return useMutation({
    mutationFn: async ({ email }: SendVerificationEmailInput) => {
      return authClient.sendVerificationEmail({
        email,
        callbackURL: options.callbackURL,
      });
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
};

// Sign In with Google
interface SignInWithGoogleOptions {
  callbackURL: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

interface SignInWithGoogleVariables {
  loginHint?: string;
}

export const useSignInWithGoogle = (options: SignInWithGoogleOptions) => {
  return useMutation({
    mutationFn: async ({ loginHint }: SignInWithGoogleVariables = {}) => {
      return new Promise<void>((resolve, reject) => {
        authClient.signIn.social(
          {
            provider: "google",
            callbackURL: options.callbackURL,
            ...(loginHint ? { loginHint } : {}),
          },
          {
            onSuccess: () => resolve(),
            onError: (ctx: BetterAuthClientOnErrorContext) =>
              reject(new Error(ctx.error.message)),
          },
        );
      });
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
};

// Sign In with Figma
interface SignInWithFigmaOptions {
  callbackURL: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const useSignInWithFigma = (options: SignInWithFigmaOptions) => {
  return useMutation({
    mutationFn: async () => {
      return new Promise<void>((resolve, reject) => {
        authClient.signIn.social(
          {
            provider: "figma",
            callbackURL: options.callbackURL,
          },
          {
            onSuccess: () => resolve(),
            onError: (ctx: BetterAuthClientOnErrorContext) =>
              reject(new Error(ctx.error.message)),
          },
        );
      });
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
};

// Sign In with Shopify
interface SignInWithShopifyOptions {
  callbackURL: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

interface SignInWithShopifyVariables {
  loginHint?: string;
}

export const useSignInWithShopify = (options: SignInWithShopifyOptions) => {
  return useMutation({
    mutationFn: async ({ loginHint }: SignInWithShopifyVariables = {}) => {
      return new Promise<void>((resolve, reject) => {
        authClient.signIn.oauth2(
          {
            providerId: "shopify",
            callbackURL: options.callbackURL,
            ...(loginHint ? { loginHint } : {}),
          },
          {
            onSuccess: () => resolve(),
            onError: (ctx: BetterAuthClientOnErrorContext) =>
              reject(new Error(ctx.error.message)),
          },
        );
      });
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
};
